import React from "react";
import { Link } from "react-router-dom";
import { useFetchVideos } from "../hooks/queries/useFetchVideos";
import { useFetchRoutes } from "../hooks/queries/useFetchRoutes";
import { useFetchReports } from "../hooks/queries/useFetchReports";
import { useFetchRouteStats } from "../hooks/queries/useFetchRouteStats";
import { VideoCameraIcon, MapIcon, ExclamationTriangleIcon, HomeIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../hooks/auth/useAuth";
import DashboardRoutesMap from "../components/routes/DashboardRoutesMap";
import TriCoinHistory from "../components/ui/TriCoinHistory";

export default function Dashboard() {
  const { data: videosData } = useFetchVideos({ pageSize: 1 });
  const { data: processedVideosData } = useFetchVideos({ pageSize: 1, processed: "true" });
  const { data: routesData } = useFetchRoutes(1, 50);
  const { data: routeStats } = useFetchRouteStats();
  const { data: reportsData } = useFetchReports(1, 1);
  const { data: pendingReportsData } = useFetchReports(1, 1, { reviewFilter: "pending" });
  const { user } = useAuth();

  // Extract arrays from pagination responses
  const routes = Array.isArray(routesData) ? routesData : (routesData?.results ?? []);

  const videosCount = videosData?.count ?? 0;
  const processedVideos = processedVideosData?.count ?? 0;
  const routesCount = routeStats?.count ?? routes.length;
  const totalDistanceKm = routeStats?.total_distance_km ?? 0;
  const reportsCount = reportsData?.count ?? 0;
  const pendingReports = pendingReportsData?.count ?? 0;

  const getInitials = (user) => {
    if (user?.username) {
      return user.username.slice(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return "U";
  };

  const quickActions = [
    { name: "Upload Video", path: "/videos" },
    { name: "Upload Route", path: "/routes" },
    { name: "View Heatmap", path: "/routes/map?heatmap=1" },
    { name: "Plan Route", path: "/routes/map?mode=plan" },
  ];

  const StatCard = ({ to, label, Icon, value, caption }) => {
    return (
      <Link to={to} className="block w-[184px] h-[148px]">
        <div
          className="w-[184px] h-[148px] bg-[#FDFEFF] rounded-[16px] p-[24px]"
          style={{ fontFamily: "'Open Sans', sans-serif" }}
        >
          <div className="h-full flex flex-col items-start">
            <div className="flex items-center gap-[8px]">
              <span className="text-[14px] font-light leading-[22px] text-[#333333]">{label}</span>
              <Icon className="h-[18px] w-[18px] text-[#333333]" />
            </div>
            <div className="mt-[10px] text-[22px] font-extrabold leading-[34px] text-[#333333]">{value}</div>
            <div className="mt-auto text-[14px] font-normal leading-[22px] text-[#939393]">{caption}</div>
          </div>
        </div>
      </Link>
    );
  };

  const ProfileCard = ({ user }) => {
    const initials = getInitials(user);

    return (
      <div
        className="w-[181px] h-[184px] bg-[#FDFEFF] rounded-[16px] p-[24px]"
        style={{ fontFamily: "'Open Sans', sans-serif" }}
      >
        <div className="w-[133px] h-[136px] flex flex-col items-start gap-[5px]">
          <div className="text-[16px] font-light leading-[25px] text-[#333333]">My profile</div>
          <div className="w-[133px] h-[87px] bg-white rounded-[4px] p-[10px] flex items-center justify-center">
            <div
              className="w-[67px] h-[67px] rounded-[53px] flex items-center justify-center"
              style={{ background: "linear-gradient(138.22deg, #FF5197 17.28%, #4B0CC0 74.45%)" }}
            >
              <span className="text-[16px] font-normal leading-[25px] text-white">{initials}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="min-h-screen bg-[#F5F5F5] pt-[32px] px-[32px] pb-[40px]"
      style={{ fontFamily: "'Open Sans', sans-serif" }}
    >
      {/* Page Header */}
      <div className="w-full h-[89px] bg-[#FDFEFF] rounded-[8px] mb-8">
        <div className="h-full px-[24px] py-[16px] flex flex-col items-start justify-between">
          <div className="flex items-center gap-[8px]">
            <h1 className="text-[20px] font-extrabold leading-[31px] text-[#333333]">Dashboard</h1>
            <HomeIcon className="h-6 w-6 text-[#333333]" />
          </div>
          <p className="text-[14px] font-light leading-[22px] text-[#333333]">
            Infrastructure Analysis &amp; Monitoring Overview
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex w-full h-[calc(100vh-185px)] min-h-[640px] items-stretch gap-[32px]">
        {/* Left Sidebar - Stats */}
        <div className="w-[184px] shrink-0">
          <div className="w-[184px] h-full min-h-[476px] flex flex-col gap-[16px]">
            <StatCard
              to="/videos"
              label="Videos"
              Icon={VideoCameraIcon}
              value={videosCount}
              caption={`${processedVideos} processed`}
            />
            <StatCard
              to="/routes"
              label="Routes"
              Icon={MapIcon}
              value={routesCount}
              caption={`${totalDistanceKm.toFixed(1)} km total`}
            />
            <StatCard
              to="/reports"
              label="Reports"
              Icon={ExclamationTriangleIcon}
              value={reportsCount}
              caption={`${pendingReports} pending review`}
            />
          </div>
        </div>

        {/* Center - Routes Map */}
        <div className="flex-1 min-w-0 h-full min-h-[476px]">
          <DashboardRoutesMap routes={routes} className="h-full" />
        </div>

        {/* Right Column - Quick Actions, Profile & TriCoin History */}
        <div className="w-[400px] shrink-0 flex flex-col items-start justify-start gap-[24px] h-full min-h-[476px] overflow-y-auto">
          {/* Quick Actions */}
          <div
            className="w-full bg-[#FDFEFF] rounded-[16px] p-[24px]"
            style={{ fontFamily: "'Open Sans', sans-serif" }}
          >
            <div className="flex flex-col items-start gap-[16px]">
              <div className="text-[16px] font-light leading-[25px] text-[#333333]">Quick Actions</div>
              <div className="w-full flex flex-col items-start gap-[8px]">
                {quickActions.map((action) => (
                  <Link
                    key={action.name}
                    to={action.path}
                    className="w-full px-[16px] py-[8px] flex items-center gap-[8px] bg-[#F8F9FA] border border-[#E0E2E5] rounded-[4px] text-[12px] font-normal leading-[19px] text-[#333333] hover:bg-gray-100 transition-colors"
                  >
                    {action.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* My profile */}
          <ProfileCard user={user} />
          
          {/* TriCoin Rewards Section */}
          {/*
          <div
            className="w-full bg-[#FDFEFF] rounded-[16px] p-[24px]"
            style={{ fontFamily: "'Open Sans', sans-serif" }}
          >
            <div className="flex flex-col items-start gap-[16px]">
              <div className="text-[16px] font-light leading-[25px] text-[#333333]">TriCoin Rewards</div>
              <div className="w-full relative">
                <TriCoinHistory className="max-h-[400px] overflow-y-auto" />
              </div>
            </div>
          </div>
          */}
        </div>
      </div>
    </div>
  );
}
